/**
 * Main Layout Component
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Shield,
  ShieldAlert,
  Key,
  Settings,
  Menu,
  X,
  LogOut,
  Server,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { api } from '../lib/api';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeSwitcher } from './ThemeSwitcher';

interface LayoutProps {
  children: React.ReactNode;
  onLogout: () => void;
}

export function Layout({ children, onLogout }: LayoutProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [gatewayStatus, setGatewayStatus] = useState<'online' | 'offline' | 'checking'>('checking');

  const navItems = [
    { id: 'dashboard', path: '/', label: t('nav.overview'), icon: LayoutDashboard },
    { id: 'logs', path: '/logs', label: t('nav.auditLogs'), icon: FileText },
    { id: 'rules', path: '/rules', label: t('nav.moderationRules'), icon: ShieldAlert },
    { id: 'clients', path: '/clients', label: t('nav.clientsTokens'), icon: Key },
    { id: 'upstreams', path: '/upstreams', label: t('nav.upstreamApis'), icon: Server },
    { id: 'settings', path: '/settings', label: t('nav.systemSettings'), icon: Settings },
  ];

  useEffect(() => {
    // Check gateway health
    const checkHealth = async () => {
      try {
        const health = await api.getHealth();
        setGatewayStatus(health.status === 'ok' ? 'online' : 'offline');
      } catch {
        setGatewayStatus('offline');
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    try {
      // Call logout endpoint to clear cookie
      await api.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always call onLogout to update app state
      onLogout();
    }
  };

  const toggleSidebarCollapse = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', String(newState));
  };

  const currentPage = navItems.find(
    (item) => item.path === location.pathname
  )?.label || 'Dashboard';

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-50 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transform transition-all duration-300 ease-in-out',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          sidebarCollapsed ? 'lg:w-[70px]' : 'lg:w-64',
          'w-64' // Always full width on mobile
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={cn(
            "flex items-center border-b border-gray-100 dark:border-gray-800 h-16 flex-shrink-0",
            sidebarCollapsed ? "justify-center" : "px-6"
          )}>
            <div className={cn(
              'flex items-center text-primary-600 dark:text-primary-400 font-bold text-xl transition-all overflow-hidden whitespace-nowrap',
              sidebarCollapsed ? 'justify-center w-full' : 'gap-2'
            )}>
              <Shield className="w-6 h-6 flex-shrink-0 fill-current" />
              <div className={cn(
                "transition-all duration-300 ease-in-out origin-left",
                sidebarCollapsed ? "w-0 opacity-0 scale-0" : "w-auto opacity-100 scale-100"
              )}>
                <span>{t('common.appName')}</span>
              </div>
            </div>
            {!sidebarCollapsed && (
              <span className="text-xs text-gray-400 ml-auto pt-1">v{__APP_VERSION__}</span>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto overflow-x-hidden">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  title={sidebarCollapsed ? item.label : undefined}
                  className={cn(
                    'flex items-center font-medium rounded-lg transition-all relative group whitespace-nowrap',
                    sidebarCollapsed ? 'justify-center h-10 w-10 mx-auto' : 'px-4 py-2.5 gap-3 w-full',
                    isActive
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                  )}
                >
                  <item.icon size={20} className="flex-shrink-0" />
                  <span className={cn(
                    "transition-all duration-300 overflow-hidden",
                    sidebarCollapsed ? "w-0 opacity-0" : "w-auto opacity-100 ml-3"
                  )}>
                    {item.label}
                  </span>
                  
                  {/* Tooltip for collapsed state */}
                  {sidebarCollapsed && (
                    <div className="hidden lg:group-hover:block absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded whitespace-nowrap z-50 shadow-lg">
                      {item.label}
                    </div>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Collapse toggle button - Desktop only */}
          <div className="hidden lg:block p-3 border-t border-gray-100 dark:border-gray-800">
            <button
              onClick={toggleSidebarCollapse}
              className={cn(
                'flex items-center justify-center text-gray-500 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors',
                sidebarCollapsed ? 'h-10 w-10 mx-auto' : 'w-full py-2 gap-2'
              )}
              title={sidebarCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
            >
              {sidebarCollapsed ? (
                <ChevronRight size={20} />
              ) : (
                <>
                  <ChevronLeft size={20} />
                  <span className="text-sm">{t('nav.collapse')}</span>
                </>
              )}
            </button>
          </div>

          {/* User section */}
          <div className="p-3 border-t border-gray-100 dark:border-gray-800">
            <div className={cn(
              "bg-gray-50 dark:bg-gray-800/50 rounded-lg transition-all",
              sidebarCollapsed ? "p-1.5 flex flex-col items-center gap-2" : "p-3 flex items-center justify-between"
            )}>
              {sidebarCollapsed ? (
                // Collapsed view - only show avatar and logout
                <>
                  <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center text-primary-700 dark:text-primary-300 font-bold text-xs shadow-sm">
                    AD
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    title={t('nav.logout')}
                  >
                    <LogOut size={16} />
                  </button>
                </>
              ) : (
                // Expanded view
                <>
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center text-primary-700 dark:text-primary-300 font-bold text-xs flex-shrink-0">
                      AD
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{t('login.admin')}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {t('common.adminDashboard')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
                    title={t('nav.logout')}
                  >
                    <LogOut size={18} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">{currentPage}</h1>
          </div>

          <div className="flex items-center gap-4">
            <ThemeSwitcher />
            <LanguageSwitcher />
            <span className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                {gatewayStatus === 'online' && (
                  <>
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                  </>
                )}
                {gatewayStatus === 'offline' && (
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                )}
                {gatewayStatus === 'checking' && (
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-500 animate-pulse" />
                )}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                {t('common.gateway')}:{' '}
                <span
                  className={cn(
                    gatewayStatus === 'online' && 'text-green-600',
                    gatewayStatus === 'offline' && 'text-red-600',
                    gatewayStatus === 'checking' && 'text-yellow-600'
                  )}
                >
                  {gatewayStatus === 'online' && t('common.online')}
                  {gatewayStatus === 'offline' && t('common.offline')}
                  {gatewayStatus === 'checking' && t('common.checking')}
                </span>
              </span>
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}

export default Layout;

